import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { User, Search } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { getUserStatus, getUser, BRAND_NAME } from "../contants";

export default function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const navigate = useNavigate();

  const { logout } = useAuth();
  const user = getUser();
  const role = getUserStatus();
  const brandName = BRAND_NAME;
  const brandInitial = brandName?.[0]?.toUpperCase() || "V";

  const userButtonRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const keyword = encodeURIComponent(searchQuery);
    navigate(`/danh-muc-san-pham/?key=${keyword}&post_type=product`);
  };

  useEffect(() => {
    if (!openUserMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        userButtonRef.current?.contains(target) ||
        userMenuRef.current?.contains(target)
      ) {
        return;
      }
      setOpenUserMenu(false);
    };

    const handleResize = () => setOpenUserMenu(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [openUserMenu]);

  const toggleUserMenu = () => {
    if (!userButtonRef.current) return;
    const rect = userButtonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
    setOpenUserMenu((prev) => !prev);
  };

  return (
    <header className="sticky top-0 z-50 shadow-lg backdrop-blur" id="header">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex flex-nowrap items-center gap-3 md:gap-6 overflow-x-auto">
          <NavLink to="/" className="flex flex-shrink-0 items-center gap-3 text-white">
            <div className="h-11 w-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center font-black text-lg">
              {brandInitial}
            </div>
            <div className="leading-tight">
              <p className="text-sm opacity-80">{brandName}</p>
              <p className="text-xs uppercase tracking-[0.15em]">Digital Store</p>
            </div>
            <span className="ml-3 hidden text-sm font-semibold text-white/80 md:inline-flex">
              Thiết bị chính hãng · Giao nhanh
            </span>
          </NavLink>

          <div className="ml-auto flex flex-shrink-0 items-center gap-2 mt-2 ">
            <form onSubmit={handleSubmit} className="w-full max-w-[700px] w-[500px]">
              <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-3 py-1.5 shadow-inner transition focus-within:ring-2 focus-within:ring-sky-300/80">
                <Search size={16} className="text-white/70" />
                <input
                  type="search"
                  className="w-full max-w-[700px] bg-transparent text-sm text-white placeholder:text-white/60 focus:outline-none"
                  placeholder="Tìm kiếm..."
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  type="submit"
                  className="rounded-xl bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/25"
                >
                  Tìm
                </button>
              </div>
            </form>

            {role && (
              <div className="relative text-white">
                <button
                  ref={userButtonRef}
                  onClick={toggleUserMenu}
                  className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold transition hover:bg-white/20"
                >
                  <User size={20} color="white" />
                  <span>{user?.customerNamne || "Tài khoản"}</span>
                </button>

                {openUserMenu &&
                  menuPosition &&
                  typeof document !== "undefined" &&
                  createPortal(
                    <div
                      ref={userMenuRef}
                      className="fixed z-[1000] w-52 overflow-hidden rounded-xl border border-gray-100 bg-white text-slate-900 shadow-xl"
                      style={{ top: menuPosition.top, right: menuPosition.right }}
                    >
                      <p className="px-4 py-3 text-sm font-semibold">{user?.customerNamne}</p>
                      <button
                        onClick={logout}
                        className="w-full px-4 py-2 text-left text-sm font-semibold text-indigo-600 hover:bg-slate-50"
                      >
                        Đăng xuất
                      </button>
                    </div>,
                    document.body
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
